import React, { useState, useEffect, useMemo } from 'react';
import EncomendaList from '../components/EncomendaList';
import EncomendaForm from '../components/EncomendaForm';
import EncomendaBaixaWizard from '../components/EncomendaBaixaWizard';
import api from '../services/api';
import { Container, Typography, Box, Button, IconButton, Collapse, TextField, Autocomplete, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import { debounce } from 'lodash';
import { actionButtonSx, filterCardSx, pageTitleSx } from '../styles/uiTokens';

function EncomendasPage() {
  const [refreshList, setRefreshList] = useState(false);
  const [editingEncomenda, setEditingEncomenda] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [baixaWizardOpen, setBaixaWizardOpen] = useState(false);

  // Filter states
  const [filterCodigoRastreio, setFilterCodigoRastreio] = useState('');
  const [selectedRemetenteIds, setSelectedRemetenteIds] = useState([]);
  const [selectedDestinatarioIds, setSelectedDestinatarioIds] = useState([]);
  const [dataRecebimentoStart, setDataRecebimentoStart] = useState('');
  const [dataRecebimentoEnd, setDataRecebimentoEnd] = useState('');
  const [todasEncomendas, setTodasEncomendas] = useState(false);

  const [pessoaOptionsRemetente, setPessoaOptionsRemetente] = useState([]);
  const [pessoaOptionsDestinatario, setPessoaOptionsDestinatario] = useState([]);
  const [selectedRemetenteFilter, setSelectedRemetenteFilter] = useState([]);
  const [selectedDestinatarioFilter, setSelectedDestinatarioFilter] = useState([]);
  const [loadingRemetente, setLoadingRemetente] = useState(false);
  const [loadingDestinatario, setLoadingDestinatario] = useState(false);

  const debouncedFetchPessoasRemetente = useMemo(
    () => debounce((searchTerm) => {
      setLoadingRemetente(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setPessoaOptionsRemetente(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar pessoas para filtro (remetente):', error))
        .finally(() => setLoadingRemetente(false));
    }, 500),
    []
  );

  const debouncedFetchPessoasDestinatario = useMemo(
    () => debounce((searchTerm) => {
      setLoadingDestinatario(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setPessoaOptionsDestinatario(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar pessoas para filtro (destinatario):', error))
        .finally(() => setLoadingDestinatario(false));
    }, 500),
    []
  );

  useEffect(() => () => {
    debouncedFetchPessoasRemetente.cancel();
    debouncedFetchPessoasDestinatario.cancel();
  }, [debouncedFetchPessoasRemetente, debouncedFetchPessoasDestinatario]);

  // Effect to trigger list refresh when filters change
  useEffect(() => {
    setRefreshList(prev => !prev);
  }, [filterCodigoRastreio, selectedRemetenteIds, selectedDestinatarioIds, dataRecebimentoStart, dataRecebimentoEnd, todasEncomendas]);

  const handleEncomendaAdded = () => {
    setRefreshList(prev => !prev);
    setEditingEncomenda(null);
    setShowForm(false);
  };

  const handleEditEncomenda = (encomenda) => {
    setEditingEncomenda(encomenda);
    setShowForm(true);
  };

  const handleCancelEditEncomenda = () => {
    setEditingEncomenda(null);
    setShowForm(false);
  };

  const handleEncomendaDeleted = () => {
    setRefreshList(prev => !prev);
  };

  const handleShowForm = () => {
    setEditingEncomenda(null);
    setShowForm(true);
  };

  const handleOpenBaixaWizard = () => {
    setBaixaWizardOpen(true);
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={pageTitleSx}>
          Gerenciar Encomendas
        </Typography>
        <Box>
          {!showForm && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={handleOpenBaixaWizard}
                sx={{ mr: 1 }}
              >
                Baixar Encomenda
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleShowForm}
                sx={{ mr: 1 }}
              >
                Adicionar Encomenda
              </Button>
              <IconButton
                color="primary"
                onClick={() => setShowFilters(prev => !prev)}
                aria-label="toggle filters"
              >
                <FilterListIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {!showForm && (
        <Collapse in={showFilters}>
          <Box sx={filterCardSx}>
            <Typography variant="h6" gutterBottom>Filtros</Typography>
            <TextField
              label="Codigo de Rastreio"
              fullWidth
              margin="normal"
              value={filterCodigoRastreio}
              onChange={(e) => setFilterCodigoRastreio(e.target.value)}
            />
            <Autocomplete
              multiple
              options={pessoaOptionsRemetente}
              getOptionLabel={(option) => option.nome || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedRemetenteFilter}
              onChange={(event, newValue) => {
                setSelectedRemetenteFilter(newValue);
                setSelectedRemetenteIds(newValue.map((pessoa) => pessoa.id));
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) debouncedFetchPessoasRemetente(newInputValue);
                  else setPessoaOptionsRemetente([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Remetente"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingRemetente ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              multiple
              options={pessoaOptionsDestinatario}
              getOptionLabel={(option) => option.nome || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedDestinatarioFilter}
              onChange={(event, newValue) => {
                setSelectedDestinatarioFilter(newValue);
                setSelectedDestinatarioIds(newValue.map((pessoa) => pessoa.id));
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) debouncedFetchPessoasDestinatario(newInputValue);
                  else setPessoaOptionsDestinatario([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destinatario"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDestinatario ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="Recebimento (Inicio)"
              type="datetime-local"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={dataRecebimentoStart}
              onChange={(e) => setDataRecebimentoStart(e.target.value)}
            />
            <TextField
              label="Recebimento (Fim)"
              type="datetime-local"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={dataRecebimentoEnd}
              onChange={(e) => setDataRecebimentoEnd(e.target.value)}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={todasEncomendas}
                  onChange={(e) => setTodasEncomendas(e.target.checked)}
                />
              )}
              label="Todas as encomendas"
            />
          </Box>
        </Collapse>
      )}

      {showForm && (
        <EncomendaForm
          onEncomendaAdded={handleEncomendaAdded}
          initialData={editingEncomenda}
          onCancelEdit={handleCancelEditEncomenda}
        />
      )}

      {!showForm && (
        <EncomendaList
          refresh={refreshList}
          onEdit={handleEditEncomenda}
          onEncomendaDeleted={handleEncomendaDeleted}
          filterCodigoRastreio={filterCodigoRastreio}
          selectedRemetenteIds={selectedRemetenteIds}
          selectedDestinatarioIds={selectedDestinatarioIds}
          dataRecebimentoStart={dataRecebimentoStart}
          dataRecebimentoEnd={dataRecebimentoEnd}
          todasEncomendas={todasEncomendas}
        />
      )}

      <EncomendaBaixaWizard
        open={baixaWizardOpen}
        onClose={() => setBaixaWizardOpen(false)}
        onSuccess={() => setRefreshList((prev) => !prev)}
      />
    </Container>
  );
}

export default EncomendasPage;

